import React from 'react';
import { MonthlyDates, ImportantDatesState } from './types';
import { format, differenceInCalendarDays, parseISO, isValid } from 'date-fns';
import { Calendar, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
interface ViewerProps {
  periods: MonthlyDates;
  onSwitchToAdmin: () => void;
}
interface DeadlineItem {
  id: string;
  trackLabel: string;
  milestoneLabel: string;
  dateStr: string;
  dateObj: Date;
  daysRemaining: number;
}
const TRACK_PRIMARY = 'Primary Refinance';
const TRACK_SECONDARY = '2nd Homes/ Investment Homes/ Purchases';
const FIELD_LABELS: Record<keyof ImportantDatesState['primaryRefi'], string> = {
  initialLE: 'Last Day to Send Out Initial LE',
  signCD: 'Last Day to Get CD Signed',
  close: 'Last Day to Close'
};
export function Viewer({
  periods,
  onSwitchToAdmin
}: ViewerProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const allDeadlines: DeadlineItem[] = [];
  const processDate = (dateStr: string, trackLabel: string, milestoneLabel: string, period: string) => {
    if (!dateStr) return;
    const dateObj = parseISO(dateStr);
    if (!isValid(dateObj)) return;
    // Adjust for timezone offset to avoid off-by-one day issues
    const adjustedDate = new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60000);
    adjustedDate.setHours(0, 0, 0, 0);
    const daysRemaining = differenceInCalendarDays(adjustedDate, today);
    allDeadlines.push({
      id: `${period}-${trackLabel}-${milestoneLabel}-${dateStr}`,
      trackLabel,
      milestoneLabel,
      dateStr,
      dateObj: adjustedDate,
      daysRemaining
    });
  };
  // Flatten every prepped month into a single sorted deadline list.
  Object.entries(periods).forEach(([period, dates]) => {
    ;
    (['initialLE', 'signCD', 'close'] as const).forEach(field => {
      processDate(dates.primaryRefi[field], TRACK_PRIMARY, FIELD_LABELS[field], period);
      processDate(dates.secondInvestPurch[field], TRACK_SECONDARY, FIELD_LABELS[field], period);
    });
  });
  allDeadlines.sort((a, b) => a.daysRemaining - b.daysRemaining);
  const upcomingDeadlines = allDeadlines.filter(d => d.daysRemaining >= 0);
  const nextDeadline = upcomingDeadlines.length > 0 ? upcomingDeadlines[0] : null;
  if (allDeadlines.length === 0) {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="max-w-4xl mx-auto p-12 bg-white rounded-xl shadow-sm border border-slate-200 text-center" data-id="element-58">
        <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-59">
          <Calendar className="w-8 h-8 text-slate-400" data-id="element-60" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-61">
          No Deadlines Set
        </h2>
        <p className="text-slate-500 mb-6 max-w-md mx-auto" data-id="element-62">
          There are currently no important dates configured for these loan
          tracks. Switch to admin mode to set them up.
        </p>
        <button onClick={onSwitchToAdmin} className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 transition-colors" data-id="element-63">
          Enter Dates
        </button>
      </motion.div>;
  }
  const getUrgencyColor = (days: number) => {
    if (days < 0) return 'text-slate-400 bg-slate-50 border-slate-200';
    if (days <= 7) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  };
  const getUrgencyIcon = (days: number) => {
    if (days < 0) return <CheckCircle2 className="w-4 h-4" data-id="element-64" />;
    if (days <= 7) return <Clock className="w-4 h-4" data-id="element-65" />;
    return <CheckCircle2 className="w-4 h-4" data-id="element-66" />;
  };
  const getUrgencyText = (days: number) => {
    if (days < 0) return 'Passed';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `${days} days remaining`;
  };
  const sortByDate = (a: DeadlineItem, b: DeadlineItem) => a.dateObj.getTime() - b.dateObj.getTime();
  const groupedByTrack = {
    [TRACK_PRIMARY]: allDeadlines.filter(d => d.trackLabel === TRACK_PRIMARY).sort(sortByDate),
    [TRACK_SECONDARY]: allDeadlines.filter(d => d.trackLabel === TRACK_SECONDARY).sort(sortByDate)
  };
  return <div className="max-w-5xl mx-auto space-y-8" data-id="element-67">
      {nextDeadline && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-blue-900 rounded-2xl p-6 md:p-8 text-white shadow-lg overflow-hidden relative" data-id="element-68">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 opacity-10" data-id="element-69">
            <Calendar className="w-64 h-64" data-id="element-70" />
          </div>
          <div className="relative z-10" data-id="element-71">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-800/50 text-blue-100 text-sm font-medium mb-4 border border-blue-700/50" data-id="element-72">
              Next Upcoming Deadline
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-73">
              {nextDeadline.milestoneLabel}
            </h2>
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-blue-100 mt-4" data-id="element-74">
              <span className="text-lg font-medium" data-id="element-75">
                {nextDeadline.trackLabel}
              </span>
              <span className="hidden md:inline text-blue-400" data-id="element-76">•</span>
              <div className="flex items-center gap-2 text-lg font-medium" data-id="element-77">
                <Calendar className="w-5 h-5" data-id="element-78" />
                {format(nextDeadline.dateObj, 'EEEE, MMMM d, yyyy')}
              </div>
              <span className="hidden md:inline text-blue-400" data-id="element-79">•</span>
              <div className="flex items-center gap-2 text-lg font-medium" data-id="element-80">
                <Clock className="w-5 h-5" data-id="element-81" />
                {getUrgencyText(nextDeadline.daysRemaining)}
              </div>
            </div>
          </div>
        </motion.div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-82">
        {Object.entries(groupedByTrack).map(([trackName, deadlines]) => <div key={trackName} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col" data-id="element-83">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50" data-id="element-84">
              <h3 className="text-lg font-semibold text-slate-800" data-id="element-85">
                {trackName}
              </h3>
            </div>
            <div className="p-6 flex-1" data-id="element-86">
              {deadlines.length > 0 ? <div className="space-y-4" data-id="element-87">
                  {deadlines.map((deadline, index) => <motion.div initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} key={deadline.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border ${getUrgencyColor(deadline.daysRemaining)} ${deadline.daysRemaining < 0 ? 'opacity-60' : ''}`} data-id="element-88">
                      <div className="mb-3 sm:mb-0" data-id="element-89">
                        <div className={`font-medium mb-1 ${deadline.daysRemaining < 0 ? 'text-slate-400 line-through' : 'text-slate-900'}`} data-id="element-90">
                          {deadline.milestoneLabel}
                        </div>
                        <div className="text-sm opacity-80 flex items-center gap-1.5" data-id="element-91">
                          <Calendar className="w-3.5 h-3.5" data-id="element-92" />
                          {format(deadline.dateObj, 'EEE, MMM d, yyyy')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 font-medium text-sm whitespace-nowrap bg-white/50 px-3 py-1.5 rounded-md" data-id="element-93">
                        {getUrgencyIcon(deadline.daysRemaining)}
                        {getUrgencyText(deadline.daysRemaining)}
                      </div>
                    </motion.div>)}
                </div> : <div className="h-full flex flex-col items-center justify-center text-center py-8 text-slate-500 text-sm" data-id="element-94">
                  <Calendar className="w-8 h-8 text-slate-300 mb-3" data-id="element-95" />
                  No dates configured for this track.
                </div>}
            </div>
          </div>)}
      </div>
    </div>;
}